import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from 'flowbite-react';
import './App.css';

const coinImages = {
  heads: { webp: '/heads.webp', fallback: '/heads.jpg' },
  tails: { webp: '/tails.webp', fallback: '/tails.jpg' },
};

const vibrationPattern = [18, 110, 22, 95, 28, 80, 36, 65, 46, 50, 58, 35, 75, 22, 120];

function App() {
  const [result, setResult] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCoinRevealed, setIsCoinRevealed] = useState(false);
  const [error, setError] = useState('');
  const cooldownTimer = useRef(null);

  useEffect(() => {
    Object.values(coinImages).forEach(({ webp }) => {
      const image = new window.Image();
      image.src = webp;
    });

    return () => {
      if (cooldownTimer.current !== null) {
        window.clearTimeout(cooldownTimer.current);
      }
    };
  }, []);

  const handleAnimationEnd = () => {
    setIsAnimating(false);
    setIsCoinRevealed(true);
    cooldownTimer.current = window.setTimeout(() => {
      setIsFlipping(false);
      cooldownTimer.current = null;
    }, 1000);
  };

  const handleFlip = () => {
    if (isFlipping) {
      return;
    }

    if (typeof window.crypto?.getRandomValues !== 'function') {
      setError('This browser does not provide secure random number generation, so a coin flip cannot be generated.');
      return;
    }

    const randomByte = new Uint8Array(1);
    window.crypto.getRandomValues(randomByte);
    setResult(randomByte[0] & 1 ? 'tails' : 'heads');
    setIsCoinRevealed(false);
    setError('');
    setIsFlipping(true);
    setIsAnimating(true);

    if (typeof window.navigator.vibrate === 'function') {
      window.navigator.vibrate(vibrationPattern);
    }
  };

  return (
    <div className="app-shell flex flex-col items-center justify-center bg-gray-900 text-white p-6">
      <svg aria-hidden="true" className="pixel-filter-defs">
        <filter id="coin-pixelate" x="0" y="0">
          <feFlood x="4" y="4" width="2" height="2" />
          <feComposite width="12" height="12" />
          <feTile />
          <feComposite in="SourceGraphic" operator="in" />
          <feMorphology operator="dilate" radius="5" />
        </filter>
      </svg>
      <h1 className="app-title text-6xl font-extrabold mb-10 text-gradient">Heads or Tails?</h1>
      <Card className="card p-8 mb-4">
        {result && (
          <div className="coin-stage">
            <picture className="coin-picture">
              <source srcSet={coinImages[result].webp} type="image/webp" />
              <img
                src={coinImages[result].fallback}
                alt={result}
                className={`coin-image${isAnimating ? ' coin-image--flipping' : ''}${isCoinRevealed ? '' : ' coin-image--masked'}`}
                onAnimationEnd={handleAnimationEnd}
                decoding="async"
              />
            </picture>
          </div>
        )}
      </Card>
      {error && <p className="coin-error" role="alert">{error}</p>}
      <div className={`flip-button-frame${isFlipping ? ' flip-button-frame--disabled' : ''}`}>
        <Button onClick={handleFlip} className="button" disabled={isFlipping}>
          Flip Coin
        </Button>
        <svg className="flip-button-snake" viewBox="0 0 100 50" preserveAspectRatio="none" aria-hidden="true">
          <path d="M 13 1 H 87 Q 99 1 99 13 V 37 Q 99 49 87 49 H 13 Q 1 49 1 37 V 13 Q 1 1 13 1 Z" />
        </svg>
      </div>
    </div>
  );
}

export default App;
